import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useTagSearch, useTagCategories } from '../hooks'
import type { TagItem } from '../types'
import { IconSearch, IconX } from '@/app/components/icons'

interface TagAutocompleteProps {
  readonly onTagSelect: (tag: string) => void
  readonly placeholder?: string
  readonly className?: string
}

export function TagAutocomplete({ onTagSelect, placeholder, className }: TagAutocompleteProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(undefined)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const { data: searchData, isLoading } = useTagSearch(debouncedQuery, 20, selectedCategory)
  const { data: categories } = useTagCategories()

  const tags = searchData?.tags ?? []

  // Debounce input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [inputValue])

  // Open dropdown when there are results
  useEffect(() => {
    if (debouncedQuery.length >= 1 && tags.length > 0) {
      setIsOpen(true)
    }
  }, [debouncedQuery, tags.length])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-tag-item]')
      items[activeIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  const handleSelect = useCallback(
    (tag: TagItem) => {
      onTagSelect(tag.name)
      setInputValue('')
      setDebouncedQuery('')
      setIsOpen(false)
      setActiveIndex(-1)
      inputRef.current?.focus()
    },
    [onTagSelect],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        if (e.key === 'ArrowDown' && tags.length > 0) {
          setIsOpen(true)
          setActiveIndex(0)
          e.preventDefault()
        }
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setActiveIndex((prev) => (prev < tags.length - 1 ? prev + 1 : 0))
          break
        case 'ArrowUp':
          e.preventDefault()
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : tags.length - 1))
          break
        case 'Enter':
          e.preventDefault()
          if (activeIndex >= 0 && activeIndex < tags.length) {
            const tag = tags[activeIndex]
            if (tag) handleSelect(tag)
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsOpen(false)
          setActiveIndex(-1)
          break
      }
    },
    [isOpen, tags, activeIndex, handleSelect],
  )

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      {/* Search input */}
      <div
        className="flex items-center gap-2 rounded-xl border border-tg-hint/20 bg-tg-secondary px-3 py-2.5 shadow-sm transition-all duration-150 focus-within:border-[var(--tg-theme-button-color)] focus-within:shadow-md hover:border-tg-hint/40"
      >
        <IconSearch className="h-4 w-4 shrink-0 text-tg-hint" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setActiveIndex(-1)
          }}
          onFocus={() => {
            if (tags.length > 0 && debouncedQuery.length >= 1) setIsOpen(true)
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? t('tags.search')}
          className="flex-1 bg-transparent text-sm text-tg-text outline-none"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `tag-option-${activeIndex}` : undefined}
        />
        {inputValue.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setInputValue('')
              setDebouncedQuery('')
              setIsOpen(false)
              inputRef.current?.focus()
            }}
            className="rounded-full p-0.5 text-tg-hint transition-colors hover:text-tg-text active:scale-95"
          >
            <IconX className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Category filter pills */}
      {isOpen && categories && categories.length > 0 && (
        <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setSelectedCategory(undefined)}
            className="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium transition-all duration-150 active:scale-96"
            style={{
              backgroundColor: selectedCategory === undefined
                ? 'var(--tg-theme-button-color)'
                : 'color-mix(in srgb, var(--tg-theme-button-color) 8%, var(--tg-theme-secondary-bg-color))',
              color: selectedCategory === undefined
                ? 'var(--tg-theme-button-text-color)'
                : 'var(--tg-theme-text-color)',
              border: selectedCategory === undefined
                ? '1px solid var(--tg-theme-button-color)'
                : '1px solid transparent',
            }}
          >
            {t('history.filter.all')}
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setSelectedCategory(cat.id)}
              className="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium transition-all duration-150 active:scale-96"
              style={{
                backgroundColor: selectedCategory === cat.id
                  ? `color-mix(in srgb, ${cat.color} 20%, var(--tg-theme-secondary-bg-color))`
                  : 'color-mix(in srgb, var(--tg-theme-button-color) 8%, var(--tg-theme-secondary-bg-color))',
                color: selectedCategory === cat.id ? cat.color : 'var(--tg-theme-text-color)',
                border: selectedCategory === cat.id ? `1px solid ${cat.color}` : '1px solid transparent',
              }}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && debouncedQuery.length >= 1 && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-tg-hint/20 bg-tg-secondary shadow-lg animate-slide-down"
        >
          {isLoading && (
            <li className="px-3 py-2 text-xs text-tg-hint">
              {t('common.loading')}
            </li>
          )}
          {!isLoading && tags.length === 0 && (
            <li className="px-3 py-2 text-xs text-tg-hint">
              {t('tags.noResults')}
            </li>
          )}
          {tags.map((tag, index) => (
            <li
              key={tag.name}
              id={`tag-option-${index}`}
              data-tag-item
              role="option"
              aria-selected={index === activeIndex}
              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-tg-bg"
              style={{
                backgroundColor: index === activeIndex
                  ? 'var(--tg-theme-button-color, #3b82f6)'
                  : 'transparent',
                color: index === activeIndex
                  ? 'var(--tg-theme-button-text-color, #fff)'
                  : 'var(--tg-theme-text-color, #fff)',
              }}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => handleSelect(tag)}
            >
              {/* Category color dot */}
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: tag.category_color || '#6b7280' }}
                title={tag.category_name}
              />
              <span className="flex-1 truncate">{tag.name}</span>
              <span className="shrink-0 text-xs text-tg-hint">
                {tag.category_name}
              </span>
              <span className="shrink-0 text-xs tabular-nums text-tg-hint">
                {t('tags.posts', { count: tag.post_count })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
